import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Trophy, 
  Users, 
  Clock, 
  DollarSign,
  Target,
  Flame,
  Crown,
  Zap
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface Challenge {
  id: string;
  title: string;
  description: string;
  challenge_type: string;
  entry_fee: number;
  prize_pool: number;
  max_participants: number;
  current_participants: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  rules: any;
}

interface Participant {
  user_id: string;
  score: number;
  rank: number;
}

export function LiveChallenges() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [participants, setParticipants] = useState<Record<string, Participant[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChallenges();
    setupRealtimeSubscription();
  }, []);

  const fetchChallenges = async () => {
    try {
      const { data, error } = await supabase
        .from('live_challenges')
        .select('*')
        .eq('is_active', true)
        .gte('end_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;
      setChallenges(data || []);

      // Fetch participants for each challenge
      if (data) {
        for (const challenge of data) {
          fetchParticipants(challenge.id);
        }
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipants = async (challengeId: string) => {
    try {
      const { data, error } = await supabase
        .from('challenge_participants')
        .select('user_id, score, rank')
        .eq('challenge_id', challengeId)
        .order('rank', { ascending: true })
        .limit(10);

      if (error) throw error;
      setParticipants(prev => ({ ...prev, [challengeId]: data || [] }));
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const challengeChannel = supabase
      .channel('live-challenges')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_challenges'
        },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            setChallenges(prev => prev.map(c => 
              c.id === payload.new.id ? payload.new as Challenge : c
            ));
          }
        }
      )
      .subscribe();

    const participantsChannel = supabase
      .channel('challenge-participants')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'challenge_participants'
        },
        (payload) => {
          const newData = payload.new as any;
          const oldData = payload.old as any;
          const challengeId = newData?.challenge_id || oldData?.challenge_id;
          if (challengeId) {
            fetchParticipants(challengeId);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(challengeChannel);
      supabase.removeChannel(participantsChannel);
    };
  };

  const joinChallenge = async (challengeId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to join challenges",
        variant: "destructive"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('challenge_participants')
        .insert({
          challenge_id: challengeId,
          user_id: user.id,
          entry_paid: true
        });

      if (error) throw error;

      toast({
        title: "🎯 Challenge Joined!",
        description: "Good luck! May the best bettor win.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const calculateTimeLeft = (endTime: string) => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const difference = end - now;

    if (difference <= 0) return "Ended";

    const hours = Math.floor(difference / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-40 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-6 w-6 text-purple-500 animate-pulse" />
            Live Challenges
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Compete with other bettors in real-time challenges
          </p>
        </CardHeader>
      </Card>

      {challenges.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Trophy className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No active challenges</p>
            <p className="text-sm text-muted-foreground">New challenges start soon!</p>
          </CardContent>
        </Card>
      ) : (
        challenges.map((challenge) => {
          const participantCount = challenge.current_participants;
          const maxParticipants = challenge.max_participants;
          const participationProgress = (participantCount / maxParticipants) * 100;
          const timeLeft = calculateTimeLeft(challenge.end_time);
          const challengeParticipants = participants[challenge.id] || [];

          return (
            <Card 
              key={challenge.id}
              className="border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:shadow-xl"
            >
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-500/10 rounded-lg">
                        <Trophy className="h-6 w-6 text-purple-500" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">{challenge.title}</h3>
                        <p className="text-sm text-muted-foreground">{challenge.description}</p>
                      </div>
                    </div>
                    <Badge className="bg-purple-500/20 text-purple-700 border-purple-500/30">
                      {challenge.challenge_type}
                    </Badge>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <DollarSign className="h-4 w-4 text-green-500" />
                        <span className="text-xs text-muted-foreground">Entry</span>
                      </div>
                      <div className="font-bold">${challenge.entry_fee}</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Crown className="h-4 w-4 text-yellow-500" />
                        <span className="text-xs text-muted-foreground">Prize</span>
                      </div>
                      <div className="font-bold text-yellow-600">${challenge.prize_pool}</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Users className="h-4 w-4 text-blue-500" />
                        <span className="text-xs text-muted-foreground">Players</span>
                      </div>
                      <div className="font-bold">{participantCount}/{maxParticipants}</div>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 mb-1">
                        <Clock className="h-4 w-4 text-red-500" />
                        <span className="text-xs text-muted-foreground">Ends</span>
                      </div>
                      <div className="font-bold">{timeLeft}</div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Spots Filled</span>
                      <span className="font-semibold">{participantCount} / {maxParticipants}</span>
                    </div>
                    <Progress value={participationProgress} className="h-2" />
                  </div>

                  {/* Top Participants */}
                  {challengeParticipants.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Target className="h-4 w-4" />
                        <span>Top Participants</span>
                      </div>
                      <div className="flex -space-x-2">
                        {challengeParticipants.slice(0, 5).map((p, i) => (
                          <Avatar key={i} className="border-2 border-background">
                            <AvatarFallback className="text-xs bg-primary/10">
                              #{p.rank}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {challengeParticipants.length > 5 && (
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted border-2 border-background text-xs font-semibold">
                            +{challengeParticipants.length - 5}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <Button 
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                    onClick={() => joinChallenge(challenge.id)}
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Join Challenge • ${challenge.entry_fee}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
